/*!
 * liveSnapshots.js — a #$ block's lines kept under a name, and brought back
 * © 2026 Rob Canning — GPLv3
 *
 *     #$ s1
 *     synth(uid:wall, ...)
 *     snapshot(calm)            Ctrl+Enter: the block as it is now, kept as s1's "calm"
 *     snapshot()                s1's snapshots, in the output
 *
 *     #$ s1 |                   on the head, after its name: s1's snapshots offered --
 *                               taking one (Tab, Ctrl+Space) puts its lines in place of
 *                               the block's, and its name on the head: #$ s1 calm
 *
 *     console(s1, snapshot:calm)    from the score: the section from its snapshot, then gone to
 *
 * Before a snapshot comes in, the block as it was is kept as its "last", so
 * snapshot(last) -- or taking "last" on the head -- puts it back.
 *
 * Kept in the project as text/snapshots.txt, a file to read and edit like the
 * code (and versioned with it, versions/snapshots-*.txt):
 *
 *     #@ s1 calm
 *     synth(uid:wall, ...)
 *     snapshot(calm)
 *
 *     #@ s1 wild
 *     ...
 *
 * With no project (Oscilla Live on its own) they are kept in this browser.
 * This file is the snapshots and their file; the console (oscillaLive.js)
 * reads and changes the blocks.
 */

export const SNAPSHOT_FILE = "text/snapshots.txt";
const HEAD = /^#@[ \t]+([A-Za-z_][\w-]*)[ \t]+([A-Za-z_][\w-]*)[ \t]*$/;
const PREAMBLE = "// the live console's snapshots: each a #$ block's lines, under #@ <block> <name>.\n" +
  "// snapshot(name) in a block keeps it; on the block's #$ head, after its name, Tab brings one back.\n";

/** A snapshot's or a block's name: as a scene's is written. */
export const isSnapshotName = s => /^[A-Za-z_][\w-]*$/.test(String(s ?? ""));

/** The file's text as snapshots, in order: [{ block, name, body }]. Lines before the first #@ are notes. */
export function parseSnapshots(text) {
  const out = [];
  let cur = null;
  for (const line of String(text ?? "").split("\n")) {
    const m = HEAD.exec(line);
    if (m) { cur = { block: m[1], name: m[2], lines: [] }; out.push(cur); continue; }
    if (cur) cur.lines.push(line);
  }
  return out.map(({ block, name, lines }) => {
    while (lines.length && !lines.at(-1).trim()) lines.pop();
    return { block, name, body: lines.join("\n") };
  });
}

/** The snapshots as the file says them. */
export function formatSnapshots(list) {
  return PREAMBLE + list.map(s => `\n#@ ${s.block} ${s.name}\n${s.body}${s.body ? "\n" : ""}`).join("");
}

/** The list with this snapshot in it: one already under the name replaced where it is, else added. */
export function withSnapshot(list, block, name, body) {
  const at = list.findIndex(s => s.block === block && s.name === name);
  const one = { block, name, body: String(body ?? "").replace(/\s+$/, "") };
  return at < 0 ? [...list, one] : list.map((s, i) => (i === at ? one : s));
}

/** A block's snapshots' names, as they were kept. */
export const namesOf = (list, block) => list.filter(s => s.block === block).map(s => s.name);

/** One snapshot, or null. */
export const findSnapshot = (list, block, name) => list.find(s => s.block === block && s.name === name) ?? null;

// ---- where they are kept -----------------------------------------------------------

const storageKey = project => `oscilla.liveSnapshots.${project || "live"}`;
const hasProject = project => !!project && project !== "unknown";

let cache = { project: null, list: [], loading: null };
const changed = new Set();

/** Told when the snapshots known change (loaded, kept): the console redraws its hints. */
export function onSnapshotsChanged(fn) { changed.add(fn); return () => changed.delete(fn); }
const tell = () => { for (const fn of changed) fn(); };

async function readKept(project) {
  if (!hasProject(project)) {
    try { return parseSnapshots(localStorage.getItem(storageKey(project)) || ""); } catch { return []; }
  }
  try {
    const res = await fetch(`/scores/${encodeURIComponent(project)}/${SNAPSHOT_FILE}`, { cache: "no-store" });
    return res.ok ? parseSnapshots(await res.text()) : [];
  } catch { return []; }
}

/** The project's snapshots, as last read -- and a fresh read started when it is another project's. */
export function knownSnapshots(project) {
  if (cache.project !== project) {
    cache = { project, list: [], loading: null };
    loadSnapshots(project);
  }
  return cache.list;
}

/** The project's snapshots, read now. */
export function loadSnapshots(project) {
  const loading = readKept(project).then(list => {
    if (cache.project === project && cache.loading === loading) { cache.list = list; tell(); }
    return list;
  });
  if (cache.project !== project) cache = { project, list: [], loading };
  else cache.loading = loading;
  return loading;
}

/**
 * Keep a block's lines under a name: the file read fresh, changed, written
 * back (another machine's snapshots since are kept). Resolves to true when written.
 */
export async function keepSnapshot(project, block, name, body) {
  const list = withSnapshot(await readKept(project), block, name, body);
  let ok = true;
  if (!hasProject(project)) {
    try { localStorage.setItem(storageKey(project), formatSnapshots(list)); } catch { ok = false; }
  } else {
    const form = new FormData();
    form.append("path", SNAPSHOT_FILE);
    form.append("file", new Blob([formatSnapshots(list)], { type: "text/plain" }), "snapshots.txt");
    try { ok = (await fetch(`/api/project/${encodeURIComponent(project)}/file`, { method: "POST", body: form })).ok; }
    catch { ok = false; }
  }
  if (ok) { cache = { project, list, loading: null }; tell(); }
  return ok;
}
